import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ChevronLeft } from 'lucide-react';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import {
  createConsentTemplateSchema,
  ConsentTemplateFormData,
  ConsentTemplateInputData,
  consentTemplateDefaultValues,
} from '@/features/dashboard/consentTemplates/schemas/consentTemplateSchema.ts';
import useCreateConsentTemplate from '@/features/dashboard/consentTemplates/hooks/useCreateConsentTemplate.ts';
import useGetConsentTemplateVariables from '@/features/dashboard/consentTemplates/hooks/useGetConsentTemplateVariables.ts';
import { getRequiredVariableKeys } from '@/features/dashboard/messageTemplates/types/templateVariables.utils.ts';
import { useInvalidSubmitFeedback } from '@/hooks/useInvalidSubmitFeedback.ts';
import ConsentTemplateForm from '@/features/dashboard/consentTemplates/components/ConsentTemplateForm.tsx';
import ConsentTypeStep from '@/features/dashboard/consentTemplates/components/ConsentTypeStep.tsx';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';
import { consentTypeLabels } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.utils.ts';

const MODAL_TITLE = 'Dodaj szablon zgody';

/**
 * Drugi krok stoi w osobnym komponencie, żeby `useForm` montował się dopiero ze
 * znanym rodzajem. Inaczej formularz trzymałby pola trzech wariantów naraz i
 * trzeba by je czyścić przy każdej zmianie wyboru.
 */
const ConsentTemplateFormStep = ({
  type,
  onBack,
  onClose,
}: {
  type: ConsentType;
  onBack: () => void;
  onClose: () => void;
}) => {
  const createMutation = useCreateConsentTemplate();
  const { data: variables } = useGetConsentTemplateVariables();

  const resolver = useMemo(
    () =>
      debugZodResolver(
        createConsentTemplateSchema(getRequiredVariableKeys(variables)),
      ),
    [variables],
  );

  const methods = useForm<ConsentTemplateInputData>({
    resolver,
    defaultValues: consentTemplateDefaultValues(type),
  });

  const formRef = useInvalidSubmitFeedback(methods.formState, {
    title: 'Nie można zapisać szablonu',
  });

  const onSubmit = (data: ConsentTemplateFormData) => {
    createMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <FormProvider {...methods}>
      <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
        <ModalHeader
          title={MODAL_TITLE}
          description={`Rodzaj: ${consentTypeLabels[type]} — nie da się go zmienić po zapisaniu.`}
          onClose={onClose}
        />
        <ModalContent>
          <ConsentTemplateForm />
        </ModalContent>
        <ModalFooter>
          <ActionButton
            tone="ghost"
            size="sm"
            type="button"
            className="mr-auto"
            onClick={onBack}
          >
            <ChevronLeft />
            Wstecz
          </ActionButton>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={createMutation.isPending}
          >
            Dodaj szablon
          </ActionButton>
        </ModalFooter>
      </form>
    </FormProvider>
  );
};

const CreateConsentTemplateModal = ({ onClose }: { onClose: () => void }) => {
  const [pickedType, setPickedType] = useState(ConsentType.TEMPLATE);
  const [isTypeConfirmed, setIsTypeConfirmed] = useState(false);

  return (
    <Modal onClose={onClose} className="max-w-[720px]">
      {isTypeConfirmed ? (
        <ConsentTemplateFormStep
          type={pickedType}
          onBack={() => setIsTypeConfirmed(false)}
          onClose={onClose}
        />
      ) : (
        <>
          <ModalHeader
            title={MODAL_TITLE}
            description="Zacznij od rodzaju zgody. Rodzaju nie da się zmienić po zapisaniu szablonu."
            onClose={onClose}
          />
          <ModalContent>
            <ConsentTypeStep value={pickedType} onChange={setPickedType} />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="button"
              onClick={() => setIsTypeConfirmed(true)}
            >
              Dalej
            </ActionButton>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default CreateConsentTemplateModal;
